/**
 * Dynamic Page Component
 * 
 * Renders any published page by slug (e.g., /about, /contact, /services)
 * Excludes reserved routes like /checkout, /order-success
 */

import { useParams, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, getStoreUsername } from '../lib/api'
import PuckRenderer from '../components/PuckRenderer'
import CartButton from '../components/CartButton'
import LoadingSpinner from '../components/LoadingSpinner'
import Footer from '../components/Footer'

// Reserved routes that should not be treated as dynamic pages
const RESERVED_ROUTES = ['checkout', 'order-success', 'cart', 'products']

function DynamicPage() {
  const { slug } = useParams<{ slug: string }>()
  const storeUsername = getStoreUsername()

  // Redirect reserved routes
  if (slug && RESERVED_ROUTES.includes(slug)) {
    return <Navigate to={`/${slug}`} replace />
  }

  const { data: page, isLoading, error } = useQuery({
    queryKey: ['page', slug],
    queryFn: () => api.getPublishedPage(slug || 'home'),
    enabled: !!slug,
    retry: 1,
  })

  if (isLoading) {
    return <LoadingSpinner />
  }

  // Handle page not found
  if (error || !page) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full text-center">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Halaman Tidak Ditemukan
              </h1>
              <p className="text-gray-600 mb-6">
                Halaman <span className="font-semibold">/{slug}</span> tidak tersedia
                di toko <span className="font-semibold">{storeUsername}</span>.
              </p>
              <a
                href="/"
                className="inline-flex items-center justify-center px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors"
              >
                Kembali ke Beranda
              </a>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col">
      <CartButton />

      {/* Render page content */}
      <div className="flex-1">
        <PuckRenderer data={page.grapesJsData} />
      </div>

      <Footer />
    </div>
  )
}

export default DynamicPage
